import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Shuffle, Printer, ArrowLeft, Users, UserPlus, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { getToken } from "@/lib/api";

/**
 * Orientation Day Game Sorter — 15-group sorter for the camp's orientation
 * day games. Two modes:
 *   • Non-Group — every non-Mariners/Skippers/Navigators counselor is
 *     round-robin shuffled into 15 evenly-sized groups (A–O).
 *   • Group — Mariners/Skippers/Navigators counselors are seeded into 15
 *     groups by their bunk_code per the camp's 2026 cluster spec, keeping
 *     2–3 bunk clusters together (e.g. "Skippers N2–N5").
 *
 * Both modes support drag-and-drop reassignment between groups, save the
 * arrangement to MongoDB (so it survives reloads), and offer a printable
 * view where each group is on its own page.
 */
export default function OrientationGames() {
  const [params, setParams] = useSearchParams();
  const initialType = params.get("type") === "group" ? "group" : "non_group";
  const [type, setType] = useState(initialType);
  const [doc, setDoc] = useState(null);          // currently displayed game
  const [otherDoc, setOtherDoc] = useState(null); // the other game (for has-saved indicator)
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  // Group count is only honored by the non_group game; the group game uses
  // the bunk-cluster spec which is fixed. Persist last choice across loads.
  const [groupCount, setGroupCount] = useState(() => {
    const saved = parseInt(localStorage.getItem("orientation_games_count") || "15", 10);
    return Number.isFinite(saved) && saved >= 2 && saved <= 30 ? saved : 15;
  });
  useEffect(() => {
    localStorage.setItem("orientation_games_count", String(groupCount));
  }, [groupCount]);

  const switchType = (t) => {
    setType(t);
    setParams({ type: t });
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/orientation/games");
      setDoc(r.data[type] || null);
      setOtherDoc(r.data[type === "group" ? "non_group" : "group"] || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load games");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  const draw = async () => {
    if (doc && !window.confirm("This will replace the current saved arrangement with a fresh draw. Continue?")) return;
    setDrawing(true);
    try {
      const body = { type };
      // Only the non_group game accepts a custom count — the group game
      // sizes itself from the bunk-cluster spec.
      if (type === "non_group") body.count = groupCount;
      const r = await api.post("/orientation/games/draw", body);
      setDoc(r.data);
      toast.success(`Drew ${r.data.total_members} staff into ${r.data.groups.length} groups`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draw failed");
    } finally { setDrawing(false); }
  };

  const moveTo = async (crewId, toLetter) => {
    // Optimistic update for snappy drag/drop UX.
    setDoc((cur) => {
      if (!cur) return cur;
      const next = JSON.parse(JSON.stringify(cur));
      let member = null;
      for (const g of next.groups || []) {
        const idx = (g.members || []).findIndex((m) => m.crew_id === crewId);
        if (idx >= 0) { member = g.members.splice(idx, 1)[0]; break; }
      }
      if (!member && Array.isArray(next.unassigned)) {
        const idx = next.unassigned.findIndex((m) => m.crew_id === crewId);
        if (idx >= 0) member = next.unassigned.splice(idx, 1)[0];
      }
      if (!member) return cur;
      const target = (next.groups || []).find((g) => g.letter === toLetter);
      if (!target) return cur;
      target.members = (target.members || []).concat([member]);
      target.members.sort((a, b) => ((a.last_name || a.name || "").localeCompare(b.last_name || b.name || "")));
      return next;
    });
    try {
      await api.post("/orientation/games/move-member", { type, crew_id: crewId, to_letter: toLetter });
    } catch (e) {
      toast.error("Move failed — reloading");
      load();
    }
  };

  const moveBunk = async (bunkCode, toLetter) => {
    // Move the bunk + all its counselors to another group. Optimistic UI.
    setDoc((cur) => {
      if (!cur) return cur;
      const next = JSON.parse(JSON.stringify(cur));
      const target = (next.groups || []).find((g) => g.letter === toLetter);
      if (!target) return cur;
      let movedBunk = null;
      const movedMembers = [];
      for (const g of next.groups || []) {
        if (g.letter === toLetter) continue;
        const keepBunks = [];
        for (const b of g.bunks || []) {
          if ((b.code || "").toLowerCase() === bunkCode.toLowerCase()) movedBunk = b;
          else keepBunks.push(b);
        }
        g.bunks = keepBunks;
        const keepMembers = [];
        for (const m of g.members || []) {
          if ((m.bunk_code || "").toLowerCase() === bunkCode.toLowerCase()) movedMembers.push(m);
          else keepMembers.push(m);
        }
        g.members = keepMembers;
      }
      if (movedBunk) {
        target.bunks = (target.bunks || []).concat([movedBunk]);
        target.bunks.sort((a, b) => (a.code || "").localeCompare(b.code || ""));
      }
      if (movedMembers.length) {
        target.members = (target.members || []).concat(movedMembers);
        target.members.sort((a, b) => ((a.last_name || a.name || "").localeCompare(b.last_name || b.name || "")));
      }
      return next;
    });
    try {
      await api.post("/orientation/games/move-bunk", { bunk_code: bunkCode, to_letter: toLetter });
    } catch (e) {
      toast.error("Move failed — reloading");
      load();
    }
  };

  const openPrint = () => {
    const token = getToken();
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const url = `${base}/api/orientation/games/print?type=${encodeURIComponent(type)}`;
    // Open the window SYNCHRONOUSLY inside the click handler so the
    // browser keeps the user-gesture token. If we wait for the fetch
    // to resolve and then call window.open(), every modern browser
    // (Chrome, Safari, Firefox, Edge) blocks it as a non-user popup,
    // regardless of the site's popup permission.
    const w = window.open("about:blank", "_blank");
    if (!w) {
      toast.error("Browser blocked the print window — allow popups for this site and retry");
      return;
    }
    // Show a loading state immediately while we fetch the authenticated HTML.
    w.document.write(
      `<!doctype html><html><head><title>Loading print view…</title>
       <style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#475569;display:flex;align-items:center;justify-content:center;height:100vh;margin:0}</style>
       </head><body><div>Preparing print view…</div></body></html>`
    );
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("HTTP " + r.status); return r.text(); })
      .then((html) => {
        w.document.open();
        w.document.write(html);
        w.document.close();
      })
      .catch((e) => {
        try {
          w.document.open();
          w.document.write(`<pre style="padding:24px;color:#b91c1c;font-family:monospace">Print failed: ${(e && e.message) || e}</pre>`);
          w.document.close();
        } catch (_) {}
        toast.error(e?.message || "Print failed");
      });
  };

  const isGroup = type === "group";
  const title = isGroup ? "Group Game" : "Non-Group Game";
  const subtitle = isGroup
    ? "Mariners / Skippers / Navigators counselors, seeded by bunk cluster."
    : `Every non-group counselor shuffled evenly into ${groupCount} groups.`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <Link to="/orientation" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-1">
              <ArrowLeft className="w-4 h-4" /> Back to Orientation Games
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Orientation Day Game Sorter</h1>
            <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {type === "non_group" && (
              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm">
                <span># Groups</span>
                <input
                  type="number"
                  min={2}
                  max={30}
                  value={groupCount}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (Number.isFinite(v)) setGroupCount(Math.max(2, Math.min(30, v)));
                  }}
                  className="w-16 text-center font-semibold text-slate-900 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="games-group-count-input"
                />
              </label>
            )}
            <button
              onClick={draw}
              disabled={drawing}
              className="btn-touch inline-flex items-center gap-2 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold"
              data-testid="games-draw-btn"
            >
              {drawing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shuffle className="w-4 h-4" />}
              {doc ? "Re-Draw" : `Draw ${title}`}
            </button>
            <button
              onClick={openPrint}
              disabled={!doc}
              className="btn-touch inline-flex items-center gap-2 px-5 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white font-semibold"
              data-testid="games-print-btn"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
        </div>

        {/* Type toggle */}
        <div className="flex flex-wrap gap-2 mb-5">
          <ToggleBtn active={type === "non_group"} onClick={() => switchType("non_group")} data-testid="games-toggle-non-group">
            <UserPlus className="w-4 h-4" /> Non-Group Game
          </ToggleBtn>
          <ToggleBtn active={type === "group"} onClick={() => switchType("group")} data-testid="games-toggle-group">
            <Users className="w-4 h-4" /> Group Game
          </ToggleBtn>
          {doc && (
            <div className="self-center text-xs text-slate-500 ml-2">
              <strong>{doc.total_members}</strong> staff · last drawn {prettyTimestamp(doc.drawn_at_utc)}
            </div>
          )}
        </div>

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : !doc ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-12 text-center" data-testid="games-empty-state">
            <Shuffle className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <h3 className="font-bold text-lg text-slate-900">No draw yet for today</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">Click <strong>Draw {title}</strong> to seed the 15 groups.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {doc.groups.map((g) => (
                <GroupCard
                  key={g.letter}
                  g={g}
                  mode={type}
                  dragOver={dragOver === g.letter}
                  onDragEnter={() => setDragOver(g.letter)}
                  onDragLeave={() => setDragOver((cur) => cur === g.letter ? null : cur)}
                  onDrop={(payload) => {
                    if (payload?.kind === "bunk") moveBunk(payload.id, g.letter);
                    else if (payload?.kind === "member") moveTo(payload.id, g.letter);
                    setDragOver(null);
                  }}
                  onDragStartItem={(id) => setDragId(id)}
                  onDragEndItem={() => setDragId(null)}
                  dragId={dragId}
                />
              ))}
            </div>
            {!isGroup && Array.isArray(doc.unassigned) && doc.unassigned.length > 0 && (
              <div className="mt-4 rounded-2xl border-2 border-amber-300 bg-amber-50/60 p-4" data-testid="games-unassigned">
                <h3 className="font-bold text-sm text-amber-900 mb-2">
                  ⚠️ {doc.unassigned.length} staff couldn&apos;t be auto-placed (no bunk code) — drag into a group
                </h3>
                <div className="flex flex-wrap gap-2">
                  {doc.unassigned.map((m) => (
                    <div
                      key={m.crew_id}
                      draggable
                      onDragStart={(e) => { setDragId(m.crew_id); e.dataTransfer.setData("text/plain", `member:${m.crew_id}`); }}
                      onDragEnd={() => setDragId(null)}
                      className="px-3 py-1.5 rounded-full bg-white border border-amber-300 text-sm font-medium text-amber-900 cursor-grab active:cursor-grabbing"
                      data-testid={`games-unassigned-${m.crew_id}`}
                    >
                      {m.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function GroupCard({ g, mode, dragOver, onDragEnter, onDragLeave, onDrop, onDragStartItem, onDragEndItem, dragId }) {
  const isGroup = mode === "group";
  const bunks = g.bunks || [];
  const itemCount = isGroup ? bunks.length : (g.members?.length || 0);
  const itemLabel = isGroup ? (itemCount === 1 ? "bunk" : "bunks") : (itemCount === 1 ? "member" : "members");
  return (
    <div
      onDragEnter={(e) => { e.preventDefault(); onDragEnter(); }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        e.preventDefault();
        const raw = e.dataTransfer.getData("text/plain");
        if (!raw) return;
        // Payload format: "bunk:b07" or "member:5160000123" — keeps the
        // two flavours separate without misrouting a counselor drag into
        // the move-bunk endpoint.
        const [kind, id] = raw.split(":", 2);
        if (kind && id) onDrop({ kind, id });
      }}
      className={`rounded-2xl border-2 bg-white p-4 transition ${dragOver ? "border-blue-500 bg-blue-50/50 ring-2 ring-blue-200" : "border-slate-200"}`}
      data-testid={`games-group-${g.letter}`}
    >
      <div className="flex items-start gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
          {g.letter}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-900 text-sm leading-tight" title={g.label}>{g.label}</div>
          <div className="text-xs text-slate-500">{itemCount} {itemLabel}{isGroup && g.members?.length ? ` · ${g.members.length} ctrs` : ""}</div>
        </div>
      </div>
      <div className="space-y-1 min-h-[80px]">
        {isGroup ? (
          bunks.length === 0 ? (
            <div className="text-xs text-slate-400 italic px-2 py-3 border border-dashed border-slate-200 rounded text-center">
              Drop bunks here
            </div>
          ) : (
            <ul className="space-y-1">
              {bunks.map((b) => (
                <li
                  key={b.code}
                  draggable
                  onDragStart={(e) => {
                    onDragStartItem(b.code);
                    e.dataTransfer.setData("text/plain", `bunk:${b.code}`);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragEnd={onDragEndItem}
                  className={`text-sm px-2 py-1 rounded bg-slate-50 border font-medium text-slate-800 cursor-grab active:cursor-grabbing ${dragId === b.code ? "opacity-40" : "hover:bg-slate-100 border-slate-200"}`}
                  data-testid={`games-bunk-${g.letter}-${b.code}`}
                  title="Drag to move this bunk + all its counselors to another group"
                >
                  {b.name}
                </li>
              ))}
            </ul>
          )
        ) : (
          (g.members?.length || 0) === 0 ? (
            <div className="text-xs text-slate-400 italic px-2 py-3 border border-dashed border-slate-200 rounded text-center">
              Drop staff here
            </div>
          ) : (
            g.members.map((m) => (
              <div
                key={m.crew_id}
                draggable
                onDragStart={(e) => {
                  onDragStartItem(m.crew_id);
                  e.dataTransfer.setData("text/plain", `member:${m.crew_id}`);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragEnd={onDragEndItem}
                className={`text-sm px-2 py-1 rounded cursor-grab active:cursor-grabbing border ${dragId === m.crew_id ? "opacity-40" : "hover:bg-slate-50"} border-transparent`}
                data-testid={`games-member-${m.crew_id}`}
                title={`${m.division || ""}${m.bunk_code ? " · " + m.bunk_code : ""}`}
              >
                {m.name}
              </div>
            ))
          )
        )}
      </div>
    </div>
  );
}

function ToggleBtn({ active, onClick, children, ...rest }) {
  return (
    <button
      onClick={onClick}
      className={`btn-touch inline-flex items-center gap-2 px-4 rounded-xl font-semibold text-sm ${active ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`}
      {...rest}
    >
      {children}
    </button>
  );
}

function prettyTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}
